/* SAP Sticky Add-to-Bag bar controller.
 *
 * Wired to the native cadence buy box rendered by snippets/sap-pdp-cadence-box.liquid.
 * Falls back to the Aurora product-form submit button on templates that still use it.
 *
 * Responsibilities:
 *   - IntersectionObserver scroll sentinel: show the bar only after the buy box
 *     has scrolled above the viewport (user has scrolled past it, not on initial load).
 *   - Mirror active plan-mode price into the sticky price display (one-time or sub).
 *   - Re-sync price on radio change (plan-mode) and select change (frequency).
 *   - Delegate add-to-bag action to the cadence form via requestSubmit so the
 *     cadence controller doAdd fires with its full cart-drawer refresh flow.
 *   - Re-init on shopify:section:load (theme editor re-render).
 *
 * Add action delegation (revenue-critical — DO NOT reimplement cart fetch here):
 *   Calling cadenceForm.requestSubmit() fires the form submit event, which the
 *   cadence controller intercepts via form.addEventListener('submit', ...) and
 *   calls doAdd() — the only correct add path (radio-gated selling_plan,
 *   Aurora drawer purchaseHandler refresh). This file never touches /cart/add.js.
 *
 * Money formatting matches cadence controller: strip trailing .00 so $35.00 shows $35.
 *
 * Section id is read from data-section-id on the bar element (not Liquid-interpolated),
 * keeping this file static and cacheable across all sections that use it.
 *
 * Inline JS is forbidden in section files (SAP rule). All JS lives here.
 */
(function () {
  'use strict';

  /* ── Money utility: mirrors formatMoney in sap-pdp-cadence-box.js ── */
  function formatMoney(cents) {
    var dollars = (cents / 100).toFixed(2);
    if (dollars.slice(-3) === '.00') dollars = dollars.slice(0, -3);
    return '$' + dollars;
  }

  function initStickyAtc(barEl) {
    if (!barEl) return;

    /* Hidden state is only transform: translateY(100%) — without inert the
       invisible Add-to-Bag stays in the Tab order and is triggerable by
       keyboard/SR users (WCAG 4.1.2). Mirror inert with the aria-hidden
       state; set it up-front so early-return paths (sold out, no sentinel)
       leave the permanently-hidden bar unfocusable too. */
    barEl.inert = barEl.getAttribute('aria-hidden') !== 'false';

    /* ── Locate the sentinel ── */
    /* Primary: the real Add-to-Bag button inside the cadence box buy row.
       Observing the actual button means the bar appears the instant the
       button scrolls out of the viewport — not later when the whole box
       leaves, and not prematurely on initial load.
       Fallback: Aurora product-form submit button for templates that still
       use the stock buy box. */
    var sentinel = document.querySelector('.sap-cadence-box__atc');
    var fallbackBuyBtn = null;
    if (!sentinel) {
      fallbackBuyBtn = document.querySelector('product-form [name="add"]');
      sentinel = fallbackBuyBtn;
    }
    if (!sentinel) return; /* Neither exists — bail */

    /* Sold out: the buy-box button renders disabled (cadence box sets
       data-sold-out; Aurora's native button is disabled too) — don't offer a
       sticky Add to Bag for a product that can't be added. */
    if (sentinel.disabled || sentinel.hasAttribute('data-sold-out')) return;

    /* ── Locate cadence form and price sources ── */
    var cadenceForm = document.querySelector('[data-sap-cadence-form]');
    var planRadios = cadenceForm
      ? cadenceForm.querySelectorAll('input[data-plan-mode]')
      : null;
    var freqSelect = cadenceForm
      ? cadenceForm.querySelector('[data-selling-plan-select]')
      : null;

    /* One-time price element: uses product-id embedded in the cadence form's
       data-product-id attribute on the parent .sap-cadence-box container. */
    var cadenceBox = document.querySelector('.sap-cadence-box');
    var productId = cadenceBox ? cadenceBox.dataset.productId : null;
    var onetimePriceEl = productId
      ? document.getElementById('sap-onetime-price-' + productId)
      : null;

    /* Sub price elements inside the cadence form */
    var subDisplayPriceEl = cadenceForm
      ? cadenceForm.querySelector('[data-sub-display-price]')
      : null;

    /* Sticky price container */
    var stickyPriceEl = barEl.querySelector('[data-sticky-price]');

    /* Sticky button */
    var stickyBtn = barEl.querySelector('.sap-sticky-atc__btn');

    /* ── Price mirror helpers ── */
    function getActiveMode() {
      if (!cadenceForm) return 'onetime';
      var checked = cadenceForm.querySelector('input[data-plan-mode]:checked');
      return checked ? checked.dataset.planMode : 'onetime';
    }

    /* One-time price: read displayed text from the price element, or fall back
       to formatting the raw cents value on the form dataset. */
    function getOnetimePriceText() {
      if (onetimePriceEl) {
        var txt = onetimePriceEl.textContent.trim();
        if (txt) return txt;
      }
      if (cadenceForm && cadenceForm.dataset.onetimePrice) {
        var cents = parseInt(cadenceForm.dataset.onetimePrice, 10);
        if (!isNaN(cents)) return formatMoney(cents);
      }
      return '';
    }

    /* Sub price: read live text from [data-sub-display-price] (kept in sync by
       the cadence controller when the SHIP EVERY frequency changes). */
    function getSubPriceText() {
      if (subDisplayPriceEl) {
        var txt = subDisplayPriceEl.textContent.trim();
        if (txt) return txt;
      }
      return '';
    }

    /* Sync sticky price display to current cadence box state.
       One-time mode: plain price text.
       Sub mode: struck one-time price + live sub price side-by-side. */
    function syncPrice() {
      if (!stickyPriceEl) return;
      var mode = getActiveMode();
      if (mode === 'sub') {
        var onetext = getOnetimePriceText();
        var subtext = getSubPriceText();
        /* Build: struck original + live sub price. Use inner spans with data hooks
           so verifier can target them precisely. */
        stickyPriceEl.innerHTML =
          '<s class="sap-sticky-atc__price-strike" data-sticky-price-strike>' +
          onetext +
          '</s>' +
          '<span class="sap-sticky-atc__price-sub" data-sticky-price-sub>' +
          subtext +
          '</span>';
      } else {
        var plain = getOnetimePriceText();
        stickyPriceEl.innerHTML =
          '<span class="sap-sticky-atc__price-onetime" data-sticky-price-onetime>' +
          plain +
          '</span>';
      }
    }

    /* ── Wire price-change listeners on the cadence form ── */
    if (planRadios && planRadios.length) {
      planRadios.forEach(function (radio) {
        radio.addEventListener('change', function () {
          syncPrice();
          syncPlan();
        });
      });
    }
    if (freqSelect) {
      /* Frequency change: cadence controller updates [data-sub-display-price] text
         synchronously in its own 'change' listener, which runs before ours because
         the cadence JS loads first. Reading subDisplayPriceEl.textContent here
         captures the already-updated value. */
      freqSelect.addEventListener('change', syncPrice);
    }

    /* ── Plan line: mirror the checked cadence card's label ── */
    var planRowEl = barEl.querySelector('[data-sticky-plan-row]');
    var planLabelEl = barEl.querySelector('[data-sticky-plan]');
    var changeBtn = barEl.querySelector('[data-sticky-change]');

    function syncPlan() {
      if (!planRowEl || !planLabelEl) return;
      /* No cadence form (fallback Aurora template) → keep the row hidden */
      if (!cadenceForm) return;
      var checked = cadenceForm.querySelector('input[data-plan-mode]:checked');
      var card = checked && checked.closest('label');
      var label = card && card.querySelector('.sap-cadence-box__row-label');
      var text = '';
      if (label) {
        /* Mirror the label WITHOUT the intro-offer badge pill — the bar has its
           own price display and the badge text would bloat the plan line. */
        var clone = label.cloneNode(true);
        var badge = clone.querySelector('.sap-cadence-box__intro-badge');
        if (badge) badge.remove();
        text = clone.textContent.replace(/\s+/g, ' ').trim();
      }
      if (!text) return;
      planLabelEl.textContent = text;
      planRowEl.hidden = false;
    }

    /* "Change" scrolls back to the buy box so the shopper can switch plans */
    if (changeBtn && cadenceBox) {
      changeBtn.addEventListener('click', function () {
        cadenceBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    /* ── Publish the bar's real rendered height to the shared token ──
       The stacked mobile layout is content-driven (taller than the token's
       clamp), and the chat-pill + Klaviyo-teaser lifts read
       --sap-sticky-atc-height — so publish the measured height to :root and
       everything above the bar stays in sync at every viewport. Guarded to
       >1px delta so the min-height feedback loop can't oscillate. */
    function publishHeight() {
      var h = barEl.offsetHeight;
      if (!h) return;
      var current = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sap-sticky-atc-height')
      );
      if (isNaN(current) || Math.abs(h - current) > 1) {
        document.documentElement.style.setProperty('--sap-sticky-atc-height', h + 'px');
      }
    }

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(publishHeight).observe(barEl);
    } else {
      window.addEventListener('resize', publishHeight);
    }

    /* Sync on init so the bar shows the correct price immediately */
    syncPrice();
    syncPlan();
    publishHeight();

    /* ── IntersectionObserver scroll sentinel ── */
    /* Show the bar the moment the sentinel (buy-box ATC button) exits through
       the TOP of the viewport; hide it whenever the button is on-screen or
       below the fold (initial load).

       rootMargin extends the observation root 100000px BELOW the viewport, so
       the sentinel "intersects" the whole time it sits anywhere below the top
       edge. The ONLY ratio transition is therefore exactly at buy-box exit.
       Without this, a fast fling or anchor jump can move the 56px button
       across the viewport between frames — ratio stays 0→0, the observer
       never fires, and the bar fails to appear (measured 2026-07-14: jump
       from scrollY 0→2200 left the bar hidden with the plain threshold:0
       observer). */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var aboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (aboveViewport) {
          barEl.classList.add('is-visible');
          barEl.setAttribute('aria-hidden', 'false');
          barEl.inert = false;
          /* Signal for chat widget to rise above the bar */
          document.body.classList.add('sap-sticky-visible');
        } else {
          barEl.classList.remove('is-visible');
          barEl.setAttribute('aria-hidden', 'true');
          barEl.inert = true;
          /* Allow chat widget to return to its default position */
          document.body.classList.remove('sap-sticky-visible');
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });

    observer.observe(sentinel);

    /* ── Sticky button click: delegate to cadence form ── */
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (cadenceForm) {
          /* requestSubmit fires the submit event so the cadence controller
             intercepts it and calls doAdd with the correct cart-drawer flow. */
          cadenceForm.requestSubmit();
        } else if (fallbackBuyBtn) {
          /* Fallback for templates that still use the Aurora product form */
          fallbackBuyBtn.click();
        }
      });

      /* ── Add-failure feedback ──
         The cadence box's error element sits under the buy box — off-screen
         whenever this bar is showing (the bar only appears after the buy box
         scrolls above the viewport). Without this, a failed add (sold-out
         mid-session, 422/429, network drop) reverts the sticky button with
         zero feedback. The cadence controller dispatches sap:add-error from
         its catch; show the failure in the bar itself for a few seconds. */
      var idleLabel = stickyBtn.textContent.trim();
      var errorLabelTimer = null;
      document.addEventListener('sap:add-error', function () {
        stickyBtn.textContent = "Couldn't add — try again";
        if (errorLabelTimer) clearTimeout(errorLabelTimer);
        errorLabelTimer = setTimeout(function () {
          errorLabelTimer = null;
          stickyBtn.textContent = idleLabel;
        }, 3000);
      });
    }
  }

  /* ── Bootstrap ── */
  function init() {
    /* Find all sticky ATC bars on the page (data-section-id is set in the Liquid markup) */
    document.querySelectorAll('.sap-sticky-atc[data-section-id]').forEach(initStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-wire on theme editor section re-render */
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    e.target.querySelectorAll('.sap-sticky-atc[data-section-id]').forEach(initStickyAtc);
  });
})();
